import { useState } from 'react';
import { useDebounce } from '@hooks/useDebounce';
import SubscriptionPlansList from './list';
import CreateEditPlanModal from './components/CreateEditPlanModal';
import { Receipt } from '@mui/icons-material';
import { Button } from '@components/atoms/Button';
import {
  useCreateSubscriptionPlan,
  useUpdateSubscriptionPlan,
  useDeleteSubscriptionPlan,
} from '@hooks/api/subscription-plans';
import { SubscriptionPlanTemplate } from '@models/subscription';

const SubscriptionPlansAdmin: React.FC = () => {
  // Search
  const [search, setSearch] = useState<string>('');
  const debouncedSearchValue = useDebounce(search, 500);
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  // Filter
  const [filter, setFilter] = useState<string>('ALL');
  const handleFilterChange = (filter: string) => {
    setFilter(filter);
  };

  // Sort
  const [orderBy, setOrderBy] = useState<string | null>('name');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlanTemplate | null>(null);

  // Mutations
  const createMutation = useCreateSubscriptionPlan();
  const updateMutation = useUpdateSubscriptionPlan();
  const deleteMutation = useDeleteSubscriptionPlan();

  // Handlers
  const handleCreateClick = () => {
    setEditingPlan(null);
    setIsModalOpen(true);
  };

  const handleEditPlan = (plan: SubscriptionPlanTemplate) => {
    setEditingPlan(plan);
    setIsModalOpen(true);
  };

  const handleCreate = async (data: any) => {
    // Sprint 17 develop run 2 (review finding, story-082 Warning #1): return the created plan so
    // CreateEditPlanModal can link any add-ons chosen before the plan existed, in the same
    // submit.
    const created = await createMutation.mutateAsync(data);
    setIsModalOpen(false);
    return created;
  };

  const handleUpdate = async (planId: string, data: any) => {
    await updateMutation.mutateAsync({ planId, payload: data });
    setIsModalOpen(false);
  };

  // Never throws: the hook's onError toast is the single place a failure is announced. Returns
  // false so the modals stay open and the user can cancel or retry.
  const handleDelete = async (planId: string): Promise<boolean> => {
    try {
      await deleteMutation.mutateAsync(planId);
      setIsModalOpen(false);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Receipt
            sx={{ fontSize: 28 }}
            className="text-orange-400 dark:text-orange-300"
          />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Subscription Plans
          </h1>
        </div>
        <Button variant="primary" onClick={handleCreateClick}>
          Create Plan
        </Button>
      </div>

      {/* List Component */}
      <SubscriptionPlansList
        search={debouncedSearchValue}
        filter={filter}
        orderBy={orderBy}
        orderDirection={orderDirection}
        handleSearchChange={handleSearchChange}
        handleFilterChange={handleFilterChange}
        handleRequestSort={handleRequestSort}
        onEditPlan={handleEditPlan}
      />

      {/* Create/Edit Modal */}
      <CreateEditPlanModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        saving={createMutation.isPending || updateMutation.isPending}
        deleting={deleteMutation.isPending}
        editing={!!editingPlan}
        initialData={editingPlan || undefined}
      />
    </div>
  );
};

export default SubscriptionPlansAdmin;
